import { Outlet, Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Search as SearchIcon, Menu, X } from "lucide-react";
import { api } from "@/lib/api";
import Marquee from "@/components/Marquee";
import NewsletterForm from "@/components/NewsletterForm";
import UrgencyBanner from "@/components/UrgencyBanner";

const NAV = [
  { to: "/category/halloween", label: "Halloween" },
  { to: "/category/christmas", label: "Christmas" },
  { to: "/category/couples", label: "Couples" },
  { to: "/category/kids", label: "Kids" },
  { to: "/category/group", label: "Group" },
  { to: "/category/pop-culture", label: "Pop Culture" },
  { to: "/category/party-supplies", label: "Party Supplies" },
  { to: "/articles", label: "Articles" },
];

export default function Layout() {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [pages, setPages] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/pages").then((r) => setPages(r.data)).catch(() => setPages([]));
  }, []);

  const onSearch = (e) => {
    e.preventDefault();
    if (q.trim()) {
      navigate(`/search?q=${encodeURIComponent(q.trim())}`);
      setOpen(false);
    }
  };

  return (
    <div className="min-h-screen pcs-bg-cream flex flex-col">
      {/* Top announcement marquee */}
      <Marquee />

      {/* Header */}
      <header className="sticky top-0 z-40 pcs-bg-cream border-b-2 border-black">
        <UrgencyBanner />
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 flex items-center gap-4">
          <Link
            to="/"
            data-testid="brand-link"
            className="flex items-center gap-2 group"
          >
            <span className="font-display text-2xl md:text-3xl leading-none">
              party<span className="pcs-text-pink">costume</span>shop
            </span>
            <span className="hidden md:inline-block font-mono text-[10px] uppercase tracking-widest border-2 border-black px-1.5 py-0.5 rotate-[-4deg] group-hover:rotate-0 transition-transform">
              est. 2008
            </span>
          </Link>

          <form
            onSubmit={onSearch}
            className="ml-auto hidden md:flex items-stretch flex-1 max-w-md"
            data-testid="header-search-form"
          >
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search costumes, articles, vibes..."
              className="flex-1 brut bg-white px-4 py-2 font-medium focus:outline-none focus:ring-0"
              data-testid="header-search-input"
            />
            <button
              type="submit"
              className="brut pcs-bg-pink text-white px-4 -ml-[2px] font-bold uppercase text-sm tracking-wide brut-press"
              data-testid="header-search-submit"
            >
              <SearchIcon className="w-4 h-4" strokeWidth={3} />
            </button>
          </form>

          <button
            className="md:hidden brut p-2 bg-white"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Category strip — desktop */}
        <nav className="hidden md:block border-t-2 border-black bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-8 flex flex-wrap items-center gap-x-6 gap-y-1 py-2 font-mono uppercase text-[11px] tracking-widest">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="hover:pcs-text-pink transition-colors"
              >
                {n.label}
              </Link>
            ))}
          </div>
        </nav>

        {/* Mobile menu */}
        {open && (
          <div className="md:hidden border-t-2 border-black bg-white p-4 space-y-3">
            <form onSubmit={onSearch} className="flex">
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search..."
                className="flex-1 brut bg-white px-3 py-2"
                data-testid="mobile-search-input"
              />
              <button
                type="submit"
                className="brut pcs-bg-pink text-white px-4 -ml-[2px]"
                data-testid="mobile-search-submit"
              >
                <SearchIcon className="w-4 h-4" />
              </button>
            </form>
            <div className="grid grid-cols-2 gap-2">
              {NAV.map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  onClick={() => setOpen(false)}
                  className="brut bg-white px-3 py-2 font-mono uppercase text-xs tracking-widest"
                >
                  {n.label}
                </Link>
              ))}
            </div>
          </div>
        )}
      </header>

      {/* Page content */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="border-t-2 border-black mt-16">
        <div className="pcs-bg-blue text-white">
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 grid md:grid-cols-4 gap-10">
            <div>
              <div className="font-display text-3xl mb-2">
                party<span className="text-[#FF3366]">costume</span>shop
              </div>
              <p className="text-sm opacity-80 max-w-xs">
                Hand-picked costumes &amp; party supplies from Amazon and eBay —
                assembled by people who actually like Halloween.
              </p>
              <div className="mt-4 font-mono text-[11px] uppercase tracking-widest opacity-70">
                est. 2008 · relaunched 2026
              </div>
            </div>

            <div>
              <div className="font-mono uppercase text-xs tracking-widest mb-3 opacity-70">
                Browse
              </div>
              <ul className="space-y-1.5 text-sm">
                {NAV.slice(0, 7).map((n) => (
                  <li key={n.to}>
                    <Link to={n.to} className="hover:pcs-text-pink">
                      {n.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <div className="font-mono uppercase text-xs tracking-widest mb-3 opacity-70">
                Company
              </div>
              <ul className="space-y-1.5 text-sm" data-testid="footer-pages-list">
                {pages.map((p) => (
                  <li key={p.slug}>
                    <Link
                      to={`/page/${p.slug}`}
                      data-testid={`footer-page-link-${p.slug}`}
                      className="hover:pcs-text-pink"
                    >
                      {p.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <div className="font-mono uppercase text-xs tracking-widest mb-3 opacity-70">
                Newsletter
              </div>
              <p className="text-sm mb-3 opacity-90">
                Costume drops, last-minute ideas, and one giant pre-Halloween
                gift guide.
              </p>
              <NewsletterForm />
            </div>
          </div>
        </div>
        <div className="pcs-bg-cream border-t-2 border-black">
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-2 font-mono text-[11px] uppercase tracking-widest">
            <div>© 2008–2026 partycostumeshop.com</div>
            <div className="opacity-70 flex items-center gap-4">
              <Link to="/page/affiliate-disclosure" data-testid="footer-affiliate-disclosure-link" className="hover:pcs-text-pink underline-offset-2 hover:underline">
                Affiliate disclosure: as an Amazon Associate we earn from qualifying purchases.
              </Link>
              <Link
                to="/admin/login"
                data-testid="footer-admin-link"
                className="opacity-60 hover:opacity-100 hover:pcs-text-pink"
              >
                Admin
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
